if (!customElements.get('frequently-bought-together')) {
  customElements.define(
    'frequently-bought-together',
    class FrequentlyBoughtTogether extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('[data-fbt-form]');
        this.quantityInput = this.querySelector('[data-fbt-quantity]');
        this.totalCurrent = this.querySelector('[data-fbt-total-current]');
        this.totalCompare = this.querySelector('[data-fbt-total-compare]');
        this.submitButton = this.querySelector('[data-fbt-submit]');
        this.errorEl = this.querySelector('[data-fbt-error]');
        this.baseTotal = Number(this.dataset.baseTotal || 0);
        this.baseCompare = Number(this.dataset.baseCompare || 0);
        this.discountPercent = Number(this.dataset.discountPercent || 0);
        this.discountCode = (this.dataset.discountCode || '').trim();
        this.bundleId = (this.dataset.bundleId || '').trim();

        this.quantityInput?.addEventListener('change', () => this.updateTotals());
        this.querySelector('[data-fbt-qty-up]')?.addEventListener('click', () => this.stepQuantity(1));
        this.querySelector('[data-fbt-qty-down]')?.addEventListener('click', () => this.stepQuantity(-1));
        this.form?.addEventListener('submit', this.onSubmit.bind(this));
      }

      stepQuantity(delta) {
        if (!this.quantityInput) return;
        const min = Number(this.quantityInput.min || 1);
        const max = this.quantityInput.max ? Number(this.quantityInput.max) : null;
        let value = Number(this.quantityInput.value || min) + delta;
        value = Math.max(min, value);
        if (max !== null) value = Math.min(max, value);
        this.quantityInput.value = value;
        this.updateTotals();
      }

      updateTotals() {
        const qty = Math.max(1, Number(this.quantityInput?.value || 1));
        const discountedUnit = Math.round(this.baseTotal * (100 - this.discountPercent) / 100);
        const currentTotal = discountedUnit * qty;
        const compareTotal = this.baseCompare * qty;

        if (this.totalCurrent) {
          this.totalCurrent.textContent = this.formatMoney(currentTotal);
        }
        if (this.totalCompare) {
          if (compareTotal > currentTotal) {
            this.totalCompare.textContent = this.formatMoney(compareTotal);
            this.totalCompare.hidden = false;
          } else {
            this.totalCompare.hidden = true;
          }
        }
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat;
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && format) {
          return Shopify.formatMoney(cents, format);
        }
        return (cents / 100).toFixed(2);
      }

      getLineItemProperties() {
        if (!this.bundleId) return undefined;
        return {
          _fbt_bundle: this.bundleId,
          _fbt_discount: String(this.discountPercent)
        };
      }

      getSectionsPayload(cart) {
        if (!cart || typeof cart.getSectionsToRender !== 'function') return {};
        return {
          sections: cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname
        };
      }

      async applyBundleDiscount(cart) {
        if (!this.discountCode) return null;

        const body = {
          discount: this.discountCode,
          ...this.getSectionsPayload(cart)
        };

        const response = await fetch(`${window.routes.cart_update_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        });

        return response.json();
      }

      discountWasApplied(cartState) {
        if (!cartState) return false;

        const code = this.discountCode.toLowerCase();
        const cartApplications = cartState.cart_level_discount_applications || [];
        if (
          cartApplications.some((application) => {
            const title = (application.title || '').toLowerCase();
            return title === code || title.includes(code);
          })
        ) {
          return true;
        }

        const items = cartState.items || [];
        return items.some((item) => {
          const allocations = item.line_level_discount_allocations || item.discounts || [];
          return allocations.some((allocation) => {
            const title = (allocation.title || allocation.discount_application?.title || '').toLowerCase();
            return title === code || title.includes(code);
          });
        });
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.form || this.submitButton?.getAttribute('aria-disabled') === 'true') return;

        const qty = Math.max(1, Number(this.quantityInput?.value || 1));
        const properties = this.getLineItemProperties();
        const items = Array.from(this.form.querySelectorAll('[data-variant-id]')).map((input) => {
          const item = {
            id: Number(input.dataset.variantId),
            quantity: qty
          };
          if (properties) item.properties = properties;
          return item;
        });

        if (!items.length) return;

        this.setError('');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = {
          items,
          ...this.getSectionsPayload(cart)
        };

        if (cart && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }

        fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        })
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              const message = Array.isArray(response.description)
                ? response.description.join(', ')
                : response.description || response.message;
              this.setError(message);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'frequently-bought-together',
                errors: response.description,
                message: response.message
              });
              return;
            }

            let cartState = response;

            if (this.discountCode) {
              try {
                const discountedCart = await this.applyBundleDiscount(cart);
                if (discountedCart?.status) {
                  this.setError(
                    window.fbtStrings?.discount_error ||
                      window.cartStrings?.error ||
                      'Could not apply bundle discount'
                  );
                } else if (discountedCart) {
                  cartState = discountedCart;
                  if (!this.discountWasApplied(discountedCart)) {
                    this.setError(window.fbtStrings?.discount_not_applicable || '');
                  }
                }
              } catch {
                this.setError(window.fbtStrings?.discount_error || window.cartStrings?.error || 'Cart error');
                return;
              }
            }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'frequently-bought-together' });

            if (cart && typeof cart.renderContents === 'function') {
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
              cart.renderContents(cartState);
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch(() => {
            this.setError(window.cartStrings?.error || 'Cart error');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
          });
      }

      setError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }
    }
  );
}
